// Read-aloud: the per-message control, the player's states and its notices
// (BUILD 2 C2-13).
//
// Namespace `read-aloud`; owner WP2-7 (BUILD 2). Only the owner adds keys or
// translations. Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
// No key names a language or a voice: both are chosen automatically.
import type { NamespaceCatalog } from '@/i18n/ns';
import cues from './read-aloud-cues.json';

const en = {
  ...cues.en,
  readAloud: 'Read aloud',
  stopReading: 'Stop reading',
  replay: 'Play again',
  speaking: 'Reading aloud…',
  loadingAudio: 'Preparing audio…',
  tapToListen: 'Tap to listen',
  couldNotRead: 'This reply could not be read aloud.',
  noVoiceForLanguage: 'There is no voice for this reply’s language yet.',
  rateValue: '{rate}×',
  errorUnavailable: 'Read-aloud is not available right now.',
  errorBudgetExhausted: 'Read-aloud has reached today’s limit. Try again tomorrow.',
  errorPaymentRequired:
    'Your company’s usage credit is used up. An administrator can add a card, then try again.',
};

export type ReadAloudCopyKey = keyof typeof en;

const readAloudCopy: NamespaceCatalog<ReadAloudCopyKey> = {
  bg: {
    ...cues.bg,
    readAloud: 'Прочети на глас',
    stopReading: 'Спри четенето',
    replay: 'Пусни отново',
    speaking: 'Чете се на глас…',
    loadingAudio: 'Подготвяне на звука…',
    tapToListen: 'Докоснете за слушане',
    couldNotRead: 'Този отговор не може да бъде прочетен на глас.',
    noVoiceForLanguage: 'Все още няма глас за езика на този отговор.',
    rateValue: '{rate}×',
    errorUnavailable: 'Четенето на глас не е достъпно в момента.',
    errorBudgetExhausted: 'Достигнат е лимитът за четене на глас. Опитайте по-късно.',
    errorPaymentRequired:
      'Кредитът за употреба на компанията е изчерпан. Администратор може да добави карта.',
  },
  hr: {
    ...cues.hr,
    readAloud: 'Čitaj naglas',
    stopReading: 'Zaustavi čitanje',
    replay: 'Reproduciraj ponovno',
    speaking: 'Čitanje naglas…',
    loadingAudio: 'Priprema zvuka…',
    tapToListen: 'Dodirnite za slušanje',
    couldNotRead: 'Ovaj odgovor nije moguće pročitati naglas.',
    noVoiceForLanguage: 'Glas za jezik ovog odgovora još nije dostupan.',
    rateValue: '{rate}×',
    errorUnavailable: 'Čitanje naglas trenutačno nije dostupno.',
    errorBudgetExhausted: 'Dosegnuto je ograničenje čitanja naglas. Pokušajte ponovno kasnije.',
    errorPaymentRequired:
      'Kredit tvrtke za korištenje je potrošen. Administrator može dodati karticu.',
  },
  cs: {
    ...cues.cs,
    readAloud: 'Přečíst nahlas',
    stopReading: 'Zastavit čtení',
    replay: 'Přehrát znovu',
    speaking: 'Čtu nahlas…',
    loadingAudio: 'Připravuji zvuk…',
    tapToListen: 'Klepnutím poslouchat',
    couldNotRead: 'Tuto odpověď nelze přečíst nahlas.',
    noVoiceForLanguage: 'Pro jazyk této odpovědi zatím není dostupný hlas.',
    rateValue: '{rate}×',
    errorUnavailable: 'Čtení nahlas nyní není dostupné.',
    errorBudgetExhausted: 'Byl dosažen limit čtení nahlas. Zkuste to později.',
    errorPaymentRequired: 'Kredit společnosti na používání je vyčerpán. Správce může přidat kartu.',
  },
  da: {
    ...cues.da,
    readAloud: 'Læs højt',
    stopReading: 'Stop oplæsning',
    replay: 'Afspil igen',
    speaking: 'Læser højt…',
    loadingAudio: 'Gør lyd klar…',
    tapToListen: 'Tryk for at lytte',
    couldNotRead: 'Dette svar kunne ikke læses højt.',
    noVoiceForLanguage: 'Der er endnu ingen stemme til dette svar på dets sprog.',
    rateValue: '{rate}×',
    errorUnavailable: 'Oplæsning er ikke tilgængelig lige nu.',
    errorBudgetExhausted: 'Grænsen for oplæsning er nået. Prøv igen senere.',
    errorPaymentRequired:
      'Virksomhedens brugskredit er opbrugt. En administrator kan tilføje et kort.',
  },
  nl: {
    ...cues.nl,
    readAloud: 'Voorlezen',
    stopReading: 'Voorlezen stoppen',
    replay: 'Opnieuw afspelen',
    speaking: 'Wordt voorgelezen…',
    loadingAudio: 'Audio voorbereiden…',
    tapToListen: 'Tik om te luisteren',
    couldNotRead: 'Dit antwoord kon niet worden voorgelezen.',
    noVoiceForLanguage: 'Er is nog geen stem voor de taal van dit antwoord.',
    rateValue: '{rate}×',
    errorUnavailable: 'Voorlezen is momenteel niet beschikbaar.',
    errorBudgetExhausted: 'De voorleeslimiet is bereikt. Probeer het later opnieuw.',
    errorPaymentRequired:
      'Het gebruikstegoed van je bedrijf is op. Een beheerder kan een kaart toevoegen.',
  },
  en,
  et: {
    ...cues.et,
    readAloud: 'Loe ette',
    stopReading: 'Peata ettelugemine',
    replay: 'Esita uuesti',
    speaking: 'Loen ette…',
    loadingAudio: 'Heli ettevalmistamine…',
    tapToListen: 'Kuulamiseks puuduta',
    couldNotRead: 'Seda vastust ei saanud ette lugeda.',
    noVoiceForLanguage: 'Selle vastuse keele jaoks pole veel häält.',
    rateValue: '{rate}×',
    errorUnavailable: 'Ettelugemine pole praegu saadaval.',
    errorBudgetExhausted: 'Ettelugemise limiit on täis. Proovi hiljem uuesti.',
    errorPaymentRequired: 'Ettevõtte kasutuskrediit on otsas. Haldur saab lisada kaardi.',
  },
  fi: {
    ...cues.fi,
    readAloud: 'Lue ääneen',
    stopReading: 'Lopeta lukeminen',
    replay: 'Toista uudelleen',
    speaking: 'Luetaan ääneen…',
    loadingAudio: 'Valmistellaan ääntä…',
    tapToListen: 'Kuuntele napauttamalla',
    couldNotRead: 'Tätä vastausta ei voitu lukea ääneen.',
    noVoiceForLanguage: 'Tämän vastauksen kielelle ei ole vielä ääntä.',
    rateValue: '{rate}×',
    errorUnavailable: 'Ääneen lukeminen ei ole juuri nyt käytettävissä.',
    errorBudgetExhausted: 'Ääneen lukemisen raja on saavutettu. Yritä myöhemmin uudelleen.',
    errorPaymentRequired: 'Yrityksen käyttöluotto on käytetty. Ylläpitäjä voi lisätä kortin.',
  },
  fr: {
    ...cues.fr,
    readAloud: 'Lire à voix haute',
    stopReading: 'Arrêter la lecture',
    replay: 'Relire',
    speaking: 'Lecture à voix haute…',
    loadingAudio: 'Préparation de l’audio…',
    tapToListen: 'Appuyez pour écouter',
    couldNotRead: 'Cette réponse ne peut pas être lue à voix haute.',
    noVoiceForLanguage: 'Aucune voix n’est encore disponible pour la langue de cette réponse.',
    rateValue: '{rate}×',
    errorUnavailable: 'La lecture à voix haute est momentanément indisponible.',
    errorBudgetExhausted: 'La limite de lecture à voix haute est atteinte. Réessayez plus tard.',
    errorPaymentRequired:
      'Le crédit d’utilisation de votre entreprise est épuisé. Un administrateur peut ajouter une carte.',
  },
  de: {
    ...cues.de,
    readAloud: 'Vorlesen',
    stopReading: 'Vorlesen stoppen',
    replay: 'Erneut abspielen',
    speaking: 'Wird vorgelesen…',
    loadingAudio: 'Audio wird vorbereitet…',
    tapToListen: 'Zum Anhören tippen',
    couldNotRead: 'Diese Antwort konnte nicht vorgelesen werden.',
    noVoiceForLanguage: 'Für die Sprache dieser Antwort gibt es noch keine Stimme.',
    rateValue: '{rate}×',
    errorUnavailable: 'Vorlesen ist derzeit nicht verfügbar.',
    errorBudgetExhausted: 'Das Vorleselimit ist erreicht. Versuchen Sie es später erneut.',
    errorPaymentRequired:
      'Das Nutzungsguthaben Ihres Unternehmens ist aufgebraucht. Ein Administrator kann eine Karte hinzufügen.',
  },
  el: {
    ...cues.el,
    readAloud: 'Ανάγνωση δυνατά',
    stopReading: 'Διακοπή ανάγνωσης',
    replay: 'Αναπαραγωγή ξανά',
    speaking: 'Ανάγνωση δυνατά…',
    loadingAudio: 'Προετοιμασία ήχου…',
    tapToListen: 'Πατήστε για ακρόαση',
    couldNotRead: 'Δεν ήταν δυνατή η ανάγνωση αυτής της απάντησης δυνατά.',
    noVoiceForLanguage: 'Δεν υπάρχει ακόμη φωνή για τη γλώσσα αυτής της απάντησης.',
    rateValue: '{rate}×',
    errorUnavailable: 'Η ανάγνωση δυνατά δεν είναι διαθέσιμη τώρα.',
    errorBudgetExhausted: 'Το όριο ανάγνωσης δυνατά επιτεύχθηκε. Δοκιμάστε αργότερα.',
    errorPaymentRequired:
      'Η πίστωση χρήσης της εταιρείας σας εξαντλήθηκε. Ένας διαχειριστής μπορεί να προσθέσει κάρτα.',
  },
  hu: {
    ...cues.hu,
    readAloud: 'Felolvasás',
    stopReading: 'Felolvasás leállítása',
    replay: 'Lejátszás újra',
    speaking: 'Felolvasás…',
    loadingAudio: 'Hang előkészítése…',
    tapToListen: 'Koppintson a hallgatáshoz',
    couldNotRead: 'Ezt a választ nem sikerült felolvasni.',
    noVoiceForLanguage: 'Ehhez a válaszhoz még nincs hang a nyelvén.',
    rateValue: '{rate}×',
    errorUnavailable: 'A felolvasás jelenleg nem érhető el.',
    errorBudgetExhausted: 'A felolvasási korlát elérve. Próbálja újra később.',
    errorPaymentRequired:
      'A vállalat használati kreditje elfogyott. A rendszergazda kártyát adhat hozzá.',
  },
  ga: {
    ...cues.ga,
    readAloud: 'Léigh os ard',
    stopReading: 'Stop den léamh',
    replay: 'Seinn arís',
    speaking: 'Á léamh os ard…',
    loadingAudio: 'Fuaim á hullmhú…',
    tapToListen: 'Tapáil le héisteacht',
    couldNotRead: 'Níorbh fhéidir an freagra seo a léamh os ard.',
    noVoiceForLanguage: 'Níl guth ar fáil fós do theanga an fhreagra seo.',
    rateValue: '{rate}×',
    errorUnavailable: 'Níl an léamh os ard ar fáil faoi láthair.',
    errorBudgetExhausted:
      'Baineadh amach teorainn an léimh os ard. Bain triail eile as níos déanaí.',
    errorPaymentRequired:
      'Tá creidmheas úsáide do chuideachta ídithe. Is féidir le riarthóir cárta a chur leis.',
  },
  it: {
    ...cues.it,
    readAloud: 'Leggi ad alta voce',
    stopReading: 'Ferma lettura',
    replay: 'Riproduci di nuovo',
    speaking: 'Lettura ad alta voce…',
    loadingAudio: 'Preparazione audio…',
    tapToListen: 'Tocca per ascoltare',
    couldNotRead: 'Questa risposta non può essere letta ad alta voce.',
    noVoiceForLanguage: 'Non c’è ancora una voce per la lingua di questa risposta.',
    rateValue: '{rate}×',
    errorUnavailable: 'La lettura ad alta voce non è disponibile ora.',
    errorBudgetExhausted: 'È stato raggiunto il limite di lettura ad alta voce. Riprova più tardi.',
    errorPaymentRequired:
      'Il credito di utilizzo dell’azienda è esaurito. Un amministratore può aggiungere una carta.',
  },
  lv: {
    ...cues.lv,
    readAloud: 'Lasīt skaļi',
    stopReading: 'Apturēt lasīšanu',
    replay: 'Atskaņot vēlreiz',
    speaking: 'Lasa skaļi…',
    loadingAudio: 'Sagatavo audio…',
    tapToListen: 'Pieskarieties, lai klausītos',
    couldNotRead: 'Šo atbildi nevarēja nolasīt skaļi.',
    noVoiceForLanguage: 'Šīs atbildes valodai vēl nav pieejama balss.',
    rateValue: '{rate}×',
    errorUnavailable: 'Lasīšana skaļi pašlaik nav pieejama.',
    errorBudgetExhausted: 'Sasniegts skaļās lasīšanas limits. Mēģiniet vēlāk.',
    errorPaymentRequired:
      'Uzņēmuma lietošanas kredīts ir iztērēts. Administrators var pievienot karti.',
  },
  lt: {
    ...cues.lt,
    readAloud: 'Skaityti garsiai',
    stopReading: 'Sustabdyti skaitymą',
    replay: 'Leisti dar kartą',
    speaking: 'Skaitoma garsiai…',
    loadingAudio: 'Ruošiamas garsas…',
    tapToListen: 'Palieskite, kad klausytumėtės',
    couldNotRead: 'Šio atsakymo nepavyko perskaityti garsiai.',
    noVoiceForLanguage: 'Šio atsakymo kalbai dar nėra balso.',
    rateValue: '{rate}×',
    errorUnavailable: 'Skaitymas garsiai šiuo metu nepasiekiamas.',
    errorBudgetExhausted: 'Pasiektas skaitymo garsiai limitas. Pabandykite vėliau.',
    errorPaymentRequired:
      'Įmonės naudojimo kreditas išnaudotas. Administratorius gali pridėti kortelę.',
  },
  mt: {
    ...cues.mt,
    readAloud: 'Aqra b’leħen għoli',
    stopReading: 'Waqqaf il-qari',
    replay: 'Ilgħab mill-ġdid',
    speaking: 'Qed jinqara b’leħen għoli…',
    loadingAudio: 'Qed jitħejja l-awdjo…',
    tapToListen: 'Agħfas biex tisma’',
    couldNotRead: 'Din it-tweġiba ma setgħetx tinqara b’leħen għoli.',
    noVoiceForLanguage: 'Għad m’hemmx vuċi għal-lingwa ta’ din it-tweġiba.',
    rateValue: '{rate}×',
    errorUnavailable: 'Il-qari b’leħen għoli mhux disponibbli bħalissa.',
    errorBudgetExhausted: 'Intlaħaq il-limitu tal-qari b’leħen għoli. Erġa’ pprova aktar tard.',
    errorPaymentRequired:
      'Il-kreditu tal-użu tal-kumpanija spiċċa. Amministratur jista’ jżid karta.',
  },
  pl: {
    ...cues.pl,
    readAloud: 'Czytaj na głos',
    stopReading: 'Zatrzymaj czytanie',
    replay: 'Odtwórz ponownie',
    speaking: 'Czytanie na głos…',
    loadingAudio: 'Przygotowywanie dźwięku…',
    tapToListen: 'Dotknij, aby słuchać',
    couldNotRead: 'Nie udało się przeczytać tej odpowiedzi na głos.',
    noVoiceForLanguage: 'Dla języka tej odpowiedzi nie ma jeszcze głosu.',
    rateValue: '{rate}×',
    errorUnavailable: 'Czytanie na głos jest teraz niedostępne.',
    errorBudgetExhausted: 'Osiągnięto limit czytania na głos. Spróbuj później.',
    errorPaymentRequired:
      'Kredyt firmy na korzystanie został wyczerpany. Administrator może dodać kartę.',
  },
  pt: {
    ...cues.pt,
    readAloud: 'Ler em voz alta',
    stopReading: 'Parar leitura',
    replay: 'Reproduzir novamente',
    speaking: 'A ler em voz alta…',
    loadingAudio: 'A preparar o áudio…',
    tapToListen: 'Toque para ouvir',
    couldNotRead: 'Não foi possível ler esta resposta em voz alta.',
    noVoiceForLanguage: 'Ainda não existe uma voz para o idioma desta resposta.',
    rateValue: '{rate}×',
    errorUnavailable: 'A leitura em voz alta não está disponível agora.',
    errorBudgetExhausted:
      'O limite de leitura em voz alta foi atingido. Tente novamente mais tarde.',
    errorPaymentRequired:
      'O crédito de utilização da empresa esgotou-se. Um administrador pode adicionar um cartão.',
  },
  ro: {
    ...cues.ro,
    readAloud: 'Citește cu voce tare',
    stopReading: 'Oprește citirea',
    replay: 'Redă din nou',
    speaking: 'Se citește cu voce tare…',
    loadingAudio: 'Se pregătește sunetul…',
    tapToListen: 'Atinge pentru ascultare',
    couldNotRead: 'Acest răspuns nu a putut fi citit cu voce tare.',
    noVoiceForLanguage: 'Încă nu există o voce pentru limba acestui răspuns.',
    rateValue: '{rate}×',
    errorUnavailable: 'Citirea cu voce tare nu este disponibilă acum.',
    errorBudgetExhausted: 'Limita de citire cu voce tare a fost atinsă. Încearcă mai târziu.',
    errorPaymentRequired:
      'Creditul de utilizare al companiei s-a epuizat. Un administrator poate adăuga un card.',
  },
  sk: {
    ...cues.sk,
    readAloud: 'Čítať nahlas',
    stopReading: 'Zastaviť čítanie',
    replay: 'Prehrať znova',
    speaking: 'Čítam nahlas…',
    loadingAudio: 'Pripravuje sa zvuk…',
    tapToListen: 'Klepnutím počúvať',
    couldNotRead: 'Túto odpoveď sa nepodarilo prečítať nahlas.',
    noVoiceForLanguage: 'Pre jazyk tejto odpovede zatiaľ nie je dostupný hlas.',
    rateValue: '{rate}×',
    errorUnavailable: 'Čítanie nahlas teraz nie je dostupné.',
    errorBudgetExhausted: 'Dosiahol sa limit čítania nahlas. Skúste to neskôr.',
    errorPaymentRequired:
      'Kredit spoločnosti na používanie sa vyčerpal. Správca môže pridať kartu.',
  },
  sl: {
    ...cues.sl,
    readAloud: 'Preberi na glas',
    stopReading: 'Ustavi branje',
    replay: 'Predvajaj znova',
    speaking: 'Branje na glas…',
    loadingAudio: 'Pripravljam zvok…',
    tapToListen: 'Tapnite za poslušanje',
    couldNotRead: 'Tega odgovora ni bilo mogoče prebrati na glas.',
    noVoiceForLanguage: 'Za jezik tega odgovora še ni glasu.',
    rateValue: '{rate}×',
    errorUnavailable: 'Branje na glas trenutno ni na voljo.',
    errorBudgetExhausted: 'Dosežena je omejitev branja na glas. Poskusite pozneje.',
    errorPaymentRequired:
      'Dobroimetje podjetja za uporabo je porabljeno. Skrbnik lahko doda kartico.',
  },
  es: {
    ...cues.es,
    readAloud: 'Leer en voz alta',
    stopReading: 'Detener lectura',
    replay: 'Reproducir de nuevo',
    speaking: 'Leyendo en voz alta…',
    loadingAudio: 'Preparando el audio…',
    tapToListen: 'Toca para escuchar',
    couldNotRead: 'No se ha podido leer esta respuesta en voz alta.',
    noVoiceForLanguage: 'Todavía no hay una voz para el idioma de esta respuesta.',
    rateValue: '{rate}×',
    errorUnavailable: 'La lectura en voz alta no está disponible ahora.',
    errorBudgetExhausted: 'Se ha alcanzado el límite de lectura en voz alta. Inténtalo más tarde.',
    errorPaymentRequired:
      'Se ha agotado el crédito de uso de tu empresa. Un administrador puede añadir una tarjeta.',
  },
  sv: {
    ...cues.sv,
    readAloud: 'Läs upp',
    stopReading: 'Sluta läsa',
    replay: 'Spela upp igen',
    speaking: 'Läser upp…',
    loadingAudio: 'Förbereder ljud…',
    tapToListen: 'Tryck för att lyssna',
    couldNotRead: 'Det gick inte att läsa upp svaret.',
    noVoiceForLanguage: 'Det finns ännu ingen röst för svaret på det här språket.',
    rateValue: '{rate}×',
    errorUnavailable: 'Uppläsning är inte tillgänglig just nu.',
    errorBudgetExhausted: 'Gränsen för uppläsning har nåtts. Försök igen senare.',
    errorPaymentRequired:
      'Företagets användningskredit är slut. En administratör kan lägga till ett kort.',
  },
};
export default readAloudCopy;
